import { IOBuffer } from 'iobuffer';
import { identifyForeign } from "./detect/identifyForeign.js";
import { isGalactic } from "./detect/isGalactic.js";
import { parseGalactic } from "./galactic/parse.js";
import { createParseContext } from "./utility/ParseContext.js";
import { SpcParserError } from "./utility/SpcParserError.js";
import { hasSignature } from "./utility/bytes.js";
import { warnNaiveDate } from "./utility/naiveDate.js";
import { isUVProbeFlat, parseUVProbeFlat } from "./uvProbe/parseFlat.js";
import { parseUVProbeOle } from "./uvProbe/parseOle.js";
export { SpcParserError } from "./utility/SpcParserError.js";
/**
 * OLE2 / Microsoft Compound File Binary magic number.
 */
const oleMagic = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
/**
 * Parses an SPC file, auto-detecting the format:
 * Thermo Galactic GRAMS SPC, or Shimadzu UVProbe (OLE2 or flat) `.spc`.
 * @param buffer - SPC file buffer.
 * @param options - parsing options.
 * @returns JSON-like object with the spectra and metadata of the file.
 * @throws {SpcParserError} With code `UNSUPPORTED_FORMAT` when the format is not
 * recognised (the message names known foreign formats, and a file shorter
 * than 32 bytes is never recognised), or `TRUNCATED` when a Galactic file
 * (at least 32 bytes) ends before the data its header announces.
 * @throws {TypeError} When `options.encoding` is not a supported encoding.
 */
export function parse(buffer, options = {}) {
    const context = createParseContext(options);
    const bytes = toBytes(buffer);
    if (hasSignature(bytes, oleMagic)) {
        return finish(parseUVProbeOle(bytes, context), context);
    }
    if (isGalactic(bytes)) {
        return finish(parseGalactic(new IOBuffer(bytes), context), context);
    }
    if (isUVProbeFlat(bytes)) {
        return finish(parseUVProbeFlat(bytes, context), context);
    }
    const foreign = identifyForeign(bytes);
    throw new SpcParserError('UNSUPPORTED_FORMAT', foreign
        ? `Unsupported SPC file: detected ${foreign}`
        : 'Unsupported SPC file: not an OLE2 compound document, a Thermo Galactic SPC, or a Shimadzu UVProbe flat file (headerless data such as a Bruker EPR .spc cannot be identified)');
}
/**
 * Attaches the warnings to a parsed file, after reporting dates that name no
 * instant.
 * @param result - what the format reader produced.
 * @param context - shared parse state.
 * @returns The complete result.
 */
function finish(result, context) {
    warnNaiveDate(result, context);
    return { ...result, warnings: context.warnings };
}
/**
 * Normalizes any accepted input into a `Uint8Array` view over its bytes.
 * @param buffer - the input data.
 * @returns A `Uint8Array` over the same bytes.
 */
function toBytes(buffer) {
    if (buffer instanceof IOBuffer) {
        return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    }
    if (ArrayBuffer.isView(buffer)) {
        return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    }
    return new Uint8Array(buffer);
}
//# sourceMappingURL=parse.js.map